/* Stripe related configuration.

NOTE: REACT_APP_STRIPE_PUBLISHABLE_KEY is mandatory environment variable.
This variable is set in a hidden file: .env
To make Stripe connection work, you also need to set Stripe's private key in the Sharetribe Console.
*/

export const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// A maximum number of days forwards during which a booking can be made.
// This is limited due to Stripe holding funds up to 90 days from the
// moment they are charged. However, US accounts can hold funds up to 2 years.
// https://docs.stripe.com/connect/manual-payouts
//
// If your marketplace is for US only, you should also be aware that available
// time slots can only be fetched for 366 days into the future.
// https://www.sharetribe.com/api-reference/marketplace.html#query-time-slots
export const dayCountAvailableForBooking = 90;

/**
 * Default merchant category code (MCC)
 * MCCs are used to classify businesses by the type of goods or services they provide.
 *
 * In this template, we use code 5734 Computer Software Stores as a default for all the connected accounts.
 *
 * See the whole list of MCC codes from https://stripe.com/docs/connect/setting-mcc#list
 */
export const defaultMCC = '5734';

/*
Stripe only supports payments in certain countries, see full list
at https://stripe.com/global

You can find the bank account formats from https://stripe.com/docs/connect/payouts-bank-accounts
*/

export const supportedCountries = [
  {
    //Australia
    code: 'AU',
    currency: 'AUD',
    accountConfig: {
      bsb: true,
      accountNumber: true,
    },
  },
  {
    // Austria
    code: 'AT',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Belgium
    code: 'BE',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    //Bulgaria
    code: 'BG',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Canada
    code: 'CA',
    currency: 'CAD',
    accountConfig: {
      transitNumber: true,
      institutionNumber: true,
      accountNumber: true,
    },
  },
  {
    //Cyprus
    code: 'CY',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    //	Czech Republic
    code: 'CZ',
    currency: 'CZK',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Denmark
    code: 'DK',
    currency: 'DKK',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Estionia
    code: 'EE',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Finland
    code: 'FI',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // France
    code: 'FR',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Germany
    code: 'DE',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Greece
    code: 'GR',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Hong Kong
    code: 'HK',
    currency: 'HKD',
    accountConfig: {
      clearingCode: true,
      branchCode: true,
      accountNumber: true,
    },
  },
  {
    // Ireland
    code: 'IE',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Italy
    code: 'IT',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Japan
    code: 'JP',
    currency: 'JPY',
    accountConfig: {
      bankName: true,
      branchName: true,
      bankCode: true,
      branchCode: true,
      accountNumber: true,
      accountOwnerName: true,
    },
  },
  {
    // Latvia
    code: 'LV',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Lithuania
    code: 'LT',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Luxembourg
    code: 'LU',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Malta
    code: 'MT',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Mexico
    code: 'MX',
    currency: 'MXN',
    accountConfig: {
      clabe: true,
    },
  },
  {
    // Netherlands
    code: 'NL',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // New Zealand
    code: 'NZ',
    currency: 'NZD',
    accountConfig: {
      accountNumber: true,
    },
  },
  {
    // Norway
    code: 'NO',
    currency: 'NOK',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Poland
    code: 'PL',
    currency: 'PLN',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Portugal
    code: 'PT',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Romania
    code: 'RO',
    currency: 'RON',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Singapore
    code: 'SG',
    currency: 'SGD',
    accountConfig: {
      bankCode: true,
      branchCode: true,
      accountNumber: true,
    },
  },
  {
    // Slovakia
    code: 'SK',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Slovenia
    code: 'SI',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Spain
    code: 'ES',
    currency: 'EUR',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Sweden
    code: 'SE',
    currency: 'SEK',
    accountConfig: {
      iban: true,
    },
  },
  {
    // Switzerland
    code: 'CH',
    currency: 'CHF',
    accountConfig: {
      iban: true,
    },
  },
  {
    // United Kingdom
    code: 'GB',
    currency: 'GBP',
    accountConfig: {
      sortCode: true,
      accountNumber: true,
    },
  },
  {
    // United States
    code: 'US',
    currency: 'USD',
    accountConfig: {
      routingNumber: true,
      accountNumber: true,
    },
  },
];

/**
 * Seller membership plans. The amounts that are actually charged are set in Stripe (on the
 * payment links, and on FOUNDING_MEMBER_PRICE_IN_SUBUNITS in server/api/stripe/founding-member.js),
 * so keep these in sync.
 *
 * Subscriptions are bought through one Stripe payment link per plan.
 * NOTE: REACT_APP_STRIPE_PAYMENT_LINK_* variables are set in a hidden file: .env
 * The current user's email and id are added to the link as 'prefilled_email' and
 * 'client_reference_id' query parameters, so that the Stripe webhook handler
 * (server/api/stripe/) can connect the subscription to the correct user.
 *
 * Keep the plan ids in sync with PLAN_PRICE_IDS in server/api/stripe/subscription.js.
 */
/**
 * Listing limits are read from the environment, so that they can be changed without a
 * code change. The server enforces the same variables (server/api/stripe/listing-quota.js).
 * NOTE: REACT_APP_LISTING_LIMIT_* variables are set in a hidden file: .env
 *
 * @param {string} value Environment variable value
 * @param {number} defaultValue Used when the value is missing or isn't a whole number >= 0
 * @returns {number}
 */
const listingLimitFromEnv = (value, defaultValue) => {
  const limit = value == null || `${value}`.trim() === '' ? NaN : Number(value);
  return Number.isInteger(limit) && limit >= 0 ? limit : defaultValue;
};

export const subscriptionPlans = [
  {
    id: 'basic',
    price: 19.99,
    listingLimit: listingLimitFromEnv(process.env.REACT_APP_LISTING_LIMIT_BASIC, 10),
    paymentLink: process.env.REACT_APP_STRIPE_PAYMENT_LINK_BASIC,
  },
  {
    id: 'power',
    price: 39.99,
    listingLimit: listingLimitFromEnv(process.env.REACT_APP_LISTING_LIMIT_POWER, 25),
    paymentLink: process.env.REACT_APP_STRIPE_PAYMENT_LINK_POWER,
  },
  {
    id: 'pro',
    price: 129.99,
    listingLimit: listingLimitFromEnv(process.env.REACT_APP_LISTING_LIMIT_PRO, 100),
    paymentLink: process.env.REACT_APP_STRIPE_PAYMENT_LINK_PRO,
  },
];

/**
 * The one-off founding member package, paid through a Stripe Checkout session that the
 * server creates (server/api/stripe/create-founding-member-checkout-session.js).
 */
export const foundingMemberPlan = {
  id: 'founding',
  price: 59.97,
  regularMonthlyPrice: 19.99,
  durationInMonths: 6,
  listingLimit: listingLimitFromEnv(
    process.env.REACT_APP_LISTING_LIMIT_FOUNDING_MEMBER_MONTHLY,
    50
  ),
  memberLimit: 100,
};

export const membershipCurrency = 'USD';
